import { useRef, useState, useEffect, useCallback } from "react";
import { Link as RouterLink } from "react-router-dom";
import Box from "@mui/material/Box";
import Badge from "@mui/material/Badge";
import MenuPopover from "./MenuPopover";
import ChatListItem, { todayKey } from "src/pages/Chat/ChatListItem";
import { useChat } from "src/Hooks/ChatContext";
import { useContentSetting } from "src/Hooks/ContentContext/ContentSettingState";
import { gr } from "src/design-system/tokens";
import icons from "src/design-system/icons";
import GrIcon from "src/design-system/components/GrIcon";
import GrButton from "src/design-system/components/GrButton";
import GrIconButton from "src/design-system/components/GrIconButton";
import GrLoader from "src/design-system/components/GrLoader";
import GrCountBadge from "src/design-system/components/GrCountBadge";
import { grPopoverPaperSx } from "src/design-system/components/GrMenu";

// Header popovers (messages, notifications): 380 wide on desktop; at phone width the
// popover keeps a 16px gutter each side. The list scrolls inside, header and footer stay put.
const paperSx = {
  width: 380,
  ml: 0,
  maxWidth: `calc(100vw - ${gr.space[8]})`,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  ...grPopoverPaperSx,
  "& > .arrow": { display: "none" },
};

const listSx = {
  maxHeight: "min(440px, calc(100vh - 240px))",
  overflowY: "auto",
  overscrollBehavior: "contain",
  scrollbarWidth: "thin",
};

const headerSx = {
  display: "flex",
  flexDirection: "column",
  gap: "2px",
  flex: "none",
  px: gr.space[4],
  py: gr.space[3],
  borderBottom: `1px solid ${gr.color.line}`,
  fontFamily: gr.font.family,
};

const titleSx = {
  m: 0,
  display: "flex",
  alignItems: "center",
  gap: gr.space[2],
  fontSize: gr.font.size.lg,
  fontWeight: gr.font.weight.semibold,
  lineHeight: gr.font.lineHeight.tight,
  color: gr.color.ink,
};

const ellipsisSx = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const subtitleSx = {
  m: 0,
  fontSize: gr.font.size.sm,
  lineHeight: gr.font.lineHeight.normal,
  color: gr.color.muted,
  ...ellipsisSx,
};

export default function ChatPopover() {
  const anchorRef = useRef(null);
  const observer = useRef(null);
  const { unreadMessageCount } = useContentSetting();
  const {
    handleSetSelectedChat,
    chatList,
    get_chat_list,
    chatLoading,
    hasMorePath,
    hasTotalPages,
    hasLoading,
    setHasLoading,
  } = useChat();
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleChatClick = (chat) => {
    handleSetSelectedChat(chat);
    handleClose();
  };

  const loadMoreData = () => {
    setHasLoading(true);
    get_chat_list(hasMorePath, "", true);
  };

  const lastBookElementRef = useCallback(
    (node) => {
      if (hasLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasTotalPages) {
          loadMoreData();
        }
      });
      if (node) observer.current.observe(node);
    },
    [hasLoading, hasTotalPages, hasMorePath, observer]
  );

  useEffect(() => {
    if (open) {
      get_chat_list();
    }
  }, [open]);

  const today = todayKey();

  return (
    <>
      <GrIconButton
        ref={anchorRef}
        aria-label="Messages"
        aria-haspopup="true"
        aria-expanded={open}
        className="notification-icons-btn"
        onClick={handleOpen}
      >
        <Badge badgeContent={unreadMessageCount} color="error">
          <GrIcon icon={icons.chat} />
        </Badge>
      </GrIconButton>
      <MenuPopover
        open={open}
        onClose={handleClose}
        anchorEl={anchorRef.current}
        sx={paperSx}
      >
        <Box sx={headerSx}>
          <Box component="h2" sx={titleSx}>
            Messages
            <GrCountBadge
              count={unreadMessageCount}
              tone="alert"
              label={`${unreadMessageCount} unread messages`}
            />
          </Box>
          <Box component="p" sx={subtitleSx}>
            You have {unreadMessageCount} unread messages
          </Box>
        </Box>
        <Box sx={listSx}>
          <Box>
            {chatLoading ? (
              <GrLoader />
            ) : (
              chatList.map((chat, i) => (
                <ChatListItem
                  key={chat?.chatId ?? i}
                  chat={chat}
                  today={today}
                  onClick={() => handleChatClick(chat)}
                />
              ))
            )}
          </Box>
          {hasTotalPages ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: gr.space[3],
              }}
            >
              <Box
                component="span"
                ref={lastBookElementRef}
                onClick={loadMoreData}
                id="load-more-feed"
                sx={{
                  cursor: "pointer",
                  fontFamily: gr.font.family,
                  fontSize: gr.font.size.sm,
                  fontWeight: gr.font.weight.semibold,
                  color: gr.color.brandDark,
                }}
              >
                {hasLoading ? (
                  <GrLoader size="sm" inline />
                ) : (
                  "Load More"
                )}
              </Box>
            </Box>
          ) : (
            ""
          )}
        </Box>
        <Box
          sx={{
            flex: "none",
            p: gr.space[2],
            borderTop: `1px solid ${gr.color.line}`,
          }}
        >
          <GrButton
            variant="text"
            fullWidth
            onClick={() => setOpen(false)}
            component={RouterLink}
            to="/chat"
          >
            View All
          </GrButton>
        </Box>
      </MenuPopover>
    </>
  );
}
